import { apiFetch } from './client';

const STAFF_TOKEN_KEY = 'hosty_staff_access_token';

export interface StaffUser {
  id: number;
  email: string;
  name: string;
  surname: string;
  role: string;
  mustChangePassword?: boolean;
}

export interface StaffTicketLinkedAccount {
  userId: number;
  hasCardOnFile: boolean;
}

export interface StaffClientOption {
  id: number;
  name: string;
  email: string;
  has_card: boolean;
}

export interface StaffSupportLicenseOption {
  license_id: number;
  pos_name: string | null;
  business_name: string | null;
  business_address: string | null;
  business_postal_code: string | null;
  business_city: string | null;
  business_province: string | null;
  business_phone: string | null;
  onsite_ready: boolean;
}

export interface StaffWhatsAppPhoneOption {
  phone: string;
  label: string;
  window_open: boolean;
  last_customer_message_at: string | null;
  window_expires_at: string | null;
}

export interface StaffTicket {
  id: number;
  customer_phone: string;
  customer_name: string | null;
  customer_email: string | null;
  user_id?: number | null;
  status: string;
  priority: string;
  service_type?: 'remote' | 'onsite';
  billing_tier?: 'standard' | 'urgent';
  accepted_hourly_rate_eur?: number | string;
  source: string;
  subject: string | null;
  category?: string;
  installation_type?: 'presencial' | 'remota' | null;
  service_address?: string | null;
  service_postal_code?: string | null;
  service_city?: string | null;
  service_province?: string | null;
  message_count?: number;
  unread_count?: number;
  last_message_at?: string;
  created_at: string;
  updated_at: string;
  linkedAccount?: StaffTicketLinkedAccount | null;
  client_charge_status?: 'none' | 'pending' | 'paid' | 'failed' | 'manual' | null;
  client_charge_total?: number | string | null;
  billable_hours?: number | string | null;
  labor_charge?: number | string | null;
  travel_cost?: number | string | null;
  work_started_at?: string | null;
  work_completed_at?: string | null;
  proposal_expires_at?: string | null;
  customer_decision_channel?: 'panel' | 'app' | 'whatsapp' | null;
  whatsapp_window?: {
    open: boolean;
    last_customer_message_at: string | null;
    expires_at: string | null;
  } | null;
}

export interface StaffAgentProfile {
  id: number;
  current_level: string;
  effective_level?: string;
  professional_role?: 'technician' | 'teamLead';
  monthly_sales_count?: number;
  monthly_sales_pending?: number;
  commission_percentage?: number;
  active_referred_clients?: number;
  active_recurring_commissions?: number;
  name?: string;
  surname?: string;
  email?: string;
}

export interface ReferralProgramLevel {
  level: string;
  minActiveClients: number;
  maxActiveClients: number | null;
  subscriptionPrice?: number;
  colorHex?: string;
}

export interface ReferralMilestoneStatus {
  activeClients: number;
  bonusAmount: number;
  reached: boolean;
  estado: 'pendiente' | 'incluido_en_payout' | 'pagado' | null;
  achievedAt: string | null;
}

export interface StaffAgentMetrics {
  tickets: {
    total: number;
    resolved: number;
    average_rating: string | null;
  };
  sales: {
    count: number;
    total_amount: number;
    commission_earned: number;
  };
  earnings: {
    tickets: number;
    ratings: number;
    commission: number;
    total: number;
  };
  pending: {
    tickets: number;
    ratings: number;
    commission: number;
    total: number;
  };
  period: string;
}

export interface StaffPayout {
  id: number;
  period_start: string;
  period_end: string;
  tickets_resolved: number;
  tickets_earnings: number;
  positive_ratings: number;
  rating_bonus: number;
  sales_commission: number;
  referral_milestone_bonus?: number;
  total_amount: number;
  status: 'pending' | 'processing' | 'paid' | 'cancelled';
  payment_method?: string | null;
  payment_reference?: string | null;
  paid_at?: string | null;
  created_at: string;
}

export interface StaffMessage {
  id: number;
  ticket_id: number;
  sender_type: 'customer' | 'agent' | 'system';
  sender_id?: number | null;
  sender_name?: string;
  sender_surname?: string;
  content: string;
  message_type: string;
  channel?: string;
  delivery_status?: 'pending' | 'sent' | 'delivered' | 'read' | 'failed' | null;
  delivery_error?: string | null;
  is_read?: boolean;
  created_at: string;
}

export function getStaffToken(): string | null {
  return localStorage.getItem(STAFF_TOKEN_KEY);
}

export function setStaffToken(token: string | null): void {
  if (token) {
    localStorage.setItem(STAFF_TOKEN_KEY, token);
  } else {
    localStorage.removeItem(STAFF_TOKEN_KEY);
  }
}

export async function staffLogin(email: string, password: string) {
  const result = await apiFetch<{
    accessToken: string;
    user: StaffUser;
    message: string;
  }>('/api/account/login', {
    method: 'POST',
    body: JSON.stringify({ email, password, appType: 'staff' }),
  });
  setStaffToken(result.accessToken);
  return result;
}

export async function staffChangePassword(currentPassword: string, newPassword: string) {
  const token = getStaffToken();
  return apiFetch<{ message: string }>('/api/account/change-password', {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    body: JSON.stringify({ currentPassword, newPassword }),
  });
}

export async function staffLogout() {
  try {
    const token = getStaffToken();
    if (token) {
      await apiFetch('/api/account/logout', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
    }
  } catch {
    // ignore
  } finally {
    setStaffToken(null);
  }
}

async function staffFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = getStaffToken();
  const headers = new Headers(options.headers);
  if (!headers.has('Content-Type') && options.body) {
    headers.set('Content-Type', 'application/json');
  }
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const response = await fetch(path, { ...options, headers, credentials: 'include' });
  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error((data as { error?: string; message?: string }).error ?? (data as { message?: string }).message ?? `Error ${response.status}`);
  }

  return data as T;
}

export const staffApi = {
  getClientOptions(email: string) {
    return staffFetch<{
      client: StaffClientOption;
      licenses: StaffSupportLicenseOption[];
      whatsapp_phones: StaffWhatsAppPhoneOption[];
    }>(
      `/api/tickets/staff/client-options?email=${encodeURIComponent(email)}`
    );
  },

  createSupportProposal(data: {
    client_email: string;
    service_type: 'remote' | 'onsite';
    billing_tier: 'standard' | 'urgent';
    license_id?: number;
    message: string;
    delivery_channel: 'panel' | 'whatsapp';
    whatsapp_phone?: string;
  }) {
    return staffFetch<{ ticketId: number; status: string }>('/api/tickets/staff/proposals', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  getAvailableTickets() {
    return staffFetch<{ tickets: StaffTicket[] }>('/api/tickets/available');
  },

  getMyTickets(status?: string) {
    const query = status ? `?status=${status}` : '';
    return staffFetch<{ tickets: StaffTicket[] }>(`/api/tickets/my-tickets${query}`);
  },

  getTicket(ticketId: number) {
    return staffFetch<{ ticket: StaffTicket }>(`/api/tickets/${ticketId}`);
  },

  acceptTicket(ticketId: number) {
    return staffFetch(`/api/tickets/${ticketId}/accept`, { method: 'POST', body: '{}' });
  },

  startTicket(ticketId: number) {
    return staffFetch<{ success: boolean; status: string }>(`/api/tickets/${ticketId}/start`, {
      method: 'POST',
      body: '{}',
    });
  },

  resolveTicket(ticketId: number, data: { resolution_notes?: string; billable_hours?: number }) {
    return staffFetch(`/api/tickets/${ticketId}/resolve`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  getProfile() {
    return staffFetch<{ agent: StaffAgentProfile }>('/api/agents/me');
  },

  getMetrics(period: 'current_month' | 'last_month' = 'current_month') {
    return staffFetch<{ metrics: StaffAgentMetrics }>(`/api/agents/metrics?period=${period}`);
  },

  getPayouts() {
    return staffFetch<{ payouts: StaffPayout[] }>('/api/agents/payouts');
  },

  getReferralProgram() {
    return staffFetch<{
      success: boolean;
      subscriptionPrice: number;
      levels: ReferralProgramLevel[];
      milestones: Array<{ activeClients: number; bonusAmount: number }>;
    }>('/api/referrals/program');
  },

  getMyReferralCode() {
    return staffFetch<{
      success: boolean;
      referralCode: { codigo: string; url?: string; urlCorta?: string };
    }>('/api/referrals/my-code');
  },

  getReferralMilestones() {
    return staffFetch<{
      success: boolean;
      activeClients: number;
      milestones: ReferralMilestoneStatus[];
    }>('/api/referrals/milestones');
  },

  getMessages(ticketId: number) {
    return staffFetch<{ messages: StaffMessage[] }>(`/api/messages/ticket/${ticketId}`);
  },

  sendMessage(ticket_id: number, content: string) {
    return staffFetch<{
      message: StaffMessage;
      ticketStatus: StaffTicket['status'];
      workStarted: boolean;
    }>('/api/messages/send', {
      method: 'POST',
      body: JSON.stringify({ ticket_id, content, message_type: 'text' }),
    });
  },

  retryMessage(messageId: number) {
    return staffFetch<{
      message: StaffMessage;
      ticketStatus: StaffTicket['status'];
      workStarted: boolean;
    }>(`/api/messages/${messageId}/retry`, {
      method: 'POST',
      body: '{}',
    });
  },

  markAsRead(ticketId: number) {
    return staffFetch(`/api/messages/ticket/${ticketId}/mark-read`, {
      method: 'PUT',
      body: '{}',
    });
  },

  updateInstallationAddress(
    ticketId: number,
    data: { business_name?: string; direccion?: string; cp?: string; poblacion?: string; provincia?: string }
  ) {
    return staffFetch<{
      success: boolean;
      business_name: string | null;
      service_address: { direccion: string | null; cp: string | null; poblacion: string | null; provincia: string | null };
    }>(`/api/tickets/${ticketId}/installation-address`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  updateTicketPriority(ticketId: number, priority: 'low' | 'normal' | 'high' | 'urgent') {
    return staffFetch<{ message: string; priority: string }>(`/api/tickets/${ticketId}/priority`, {
      method: 'PUT',
      body: JSON.stringify({ priority }),
    });
  },

  linkAccount(ticketId: number, email: string) {
    return staffFetch<{
      linkedAccount: { userId: number; email: string; name: string | null; hasCardOnFile: boolean };
    }>(`/api/tickets/${ticketId}/link-account`, {
      method: 'PUT',
      body: JSON.stringify({ email }),
    });
  },

  retryCharge(ticketId: number) {
    return staffFetch<{
      success: boolean;
      clientChargeStatus: string | null;
      clientChargeTotal: number | null;
    }>(`/api/tickets/${ticketId}/retry-charge`, { method: 'POST', body: '{}' });
  },

  async downloadRemoteClient(): Promise<void> {
    const token = getStaffToken();
    const response = await fetch('/api/agents/remote-client', {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      credentials: 'include',
    });
    if (!response.ok) {
      const data = (await response.json().catch(() => ({}))) as { error?: string; message?: string };
      throw new Error(data.error ?? data.message ?? `Error ${response.status}`);
    }
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'Hosty-Remote-Staff.exe';
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  },
};
